import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import merge from 'lodash.merge';
import { en } from './translations/en';
import { et } from './translations/et';
import { ru } from './translations/ru';
import { fi } from './translations/fi';

type Language = 'en' | 'et' | 'ru' | 'fi';
type Translations = typeof en;

interface I18nContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: Translations;
}

const translations: Record<Language, Translations> = {
  en,
  et,
  ru,
  fi,
};

const I18nContext = createContext<I18nContextType | undefined>(undefined);

const LANGUAGE_KEY = 'fireandice_language';

// Helper function to fetch and merge translations
const fetchTranslationOverrides = async (language: Language): Promise<Translations> => {
  const base = translations[language];
  const apiUrl = process.env.REACT_APP_API_URL || '';

  try {
    const res = await fetch(`${apiUrl}/api/public/translations/${language}`);
    if (!res.ok) {
      throw new Error(`HTTP ${res.status}`);
    }
    const data = await res.json();
    const overrides = data?.overrides;
    if (overrides && typeof overrides === 'object' && Object.keys(overrides).length > 0) {
      return merge({}, base, overrides) as Translations;
    }
  } catch (err) {
    console.error('Failed to load translation overrides:', err);
  }

  return base;
};

export const I18nProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Get initial language from localStorage or default to 'et' (Estonian)
  const [language, setLanguageState] = useState<Language>(() => {
    const savedLanguage = localStorage.getItem(LANGUAGE_KEY);
    if (savedLanguage === 'en' || savedLanguage === 'et' || savedLanguage === 'ru' || savedLanguage === 'fi') {
      return savedLanguage as Language;
    }
    return 'et';
  });

  // Initialize with static base translations
  const [currentTranslations, setCurrentTranslations] = useState<Translations>(translations[language]);

  // Track initial loading state - only block rendering on first load
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const isInitialMount = useRef(true);

  // Fetch and merge translation overrides when language changes
  useEffect(() => {
    let isCancelled = false;

    const loadTranslations = async () => {
      const mergedTranslations = await fetchTranslationOverrides(language);

      if (!isCancelled) {
        setCurrentTranslations(mergedTranslations);

        // Only set loading to false on initial mount
        if (isInitialMount.current) {
          isInitialMount.current = false;
          setIsInitialLoading(false);
        }
      }
    };

    loadTranslations();

    return () => {
      isCancelled = true;
    };
  }, [language]);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem(LANGUAGE_KEY, lang);
  };

  const value: I18nContextType = {
    language,
    setLanguage,
    t: currentTranslations,
  };

  // Block rendering until initial translations are loaded
  if (isInitialLoading) {
    return null;
  }

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = () => {
  const context = useContext(I18nContext);
  if (context === undefined) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return context;
};